/**
 * Consult Door B — PURE client-side slot + timezone view helpers.
 *
 * The Door B page receives bookable slots as ISO INSTANT strings from the
 * public availability function. The bookable truth is anchored in IST server
 * side (spec R3); the CLIENT chooses which timezone to *display* them in, and
 * can change it — the true moment never moves, only the rendering.
 *
 * This module is React-free + Firebase-free (Intl only), mirroring the purity
 * discipline of `slotView.ts` / `availability.ts`, so it is unit-testable and
 * never drags the SDK into the Node runner.
 */

/** One display timezone option the client can pick. `id` is an IANA zone. */
export interface TzOption {
  id: string;
  label: string;
}

/**
 * A curated set of display timezones the audience actually books from
 * (India + the Gulf, UK, Singapore, US East/West — the same spread the Door B
 * mockup offered). The client's auto-detected zone is always merged in on top
 * so nobody is ever forced into a zone that isn't theirs.
 */
export const COMMON_TZS: TzOption[] = [
  { id: 'Asia/Kolkata', label: 'India · IST' },
  { id: 'Asia/Dubai', label: 'Dubai · GST' },
  { id: 'Europe/London', label: 'London · GMT/BST' },
  { id: 'Asia/Singapore', label: 'Singapore · SGT' },
  { id: 'America/New_York', label: 'New York · ET' },
  { id: 'America/Los_Angeles', label: 'Los Angeles · PT' },
];

/** The anchored bookable-truth zone (server side). */
export const ANCHOR_TZ = 'Asia/Kolkata';

/** Best-effort detect the viewer's IANA zone; falls back to the anchor. */
export function detectClientTz(): string {
  try {
    const z = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return z || ANCHOR_TZ;
  } catch {
    return ANCHOR_TZ;
  }
}

/**
 * The timezone options to render in the "show times in" select: the common set,
 * with the detected zone guaranteed present (and first) so the client always
 * finds their own. Deduped by id.
 */
export function tzOptionsWith(detected: string): TzOption[] {
  const seen = new Set<string>();
  const out: TzOption[] = [];
  const push = (o: TzOption) => {
    if (o.id && !seen.has(o.id)) {
      seen.add(o.id);
      out.push(o);
    }
  };
  if (detected && !COMMON_TZS.some((t) => t.id === detected)) {
    push({ id: detected, label: `${shortZoneName(detected)} · your timezone` });
  }
  COMMON_TZS.forEach(push);
  return out;
}

/**
 * Legacy IANA zone aliases → their modern canonical id. Browsers still report
 * some visitors' zones under deprecated names (e.g. `Intl` on older/localised
 * systems yields `Asia/Calcutta`), which reads as outdated and contradicts our
 * "India Standard Time" copy. Normalise before display.
 */
const ZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Ashkhabad': 'Asia/Ashgabat',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Dacca': 'Asia/Dhaka',
  'Europe/Kiev': 'Europe/Kyiv',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Australia/Canberra': 'Australia/Sydney',
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  GMT: 'Etc/GMT',
};

/** Normalise a zone id to its modern canonical form. Safe for any string. */
export function canonicalZone(tz: string): string {
  return ZONE_ALIASES[tz] ?? tz;
}

/** A short, human zone name, e.g. "Kolkata", "Dubai" — the city after the slash,
 *  legacy-alias-normalised and underscore-cleaned. */
export function shortZoneName(tz: string): string {
  const canon = canonicalZone(tz);
  const city = canon.split('/').pop() || canon;
  return city.replace(/_/g, ' ');
}

/**
 * The full IANA timezone list for the optional "change timezone" control, via
 * `Intl.supportedValuesOf('timeZone')` when the runtime supports it (all modern
 * browsers do). Falls back to the curated broad set so the control is never
 * empty. Native <select> type-ahead makes this searchable without a dependency.
 */
export function allTimeZones(): string[] {
  try {
    const sv = (Intl as unknown as {
      supportedValuesOf?: (key: string) => string[];
    }).supportedValuesOf;
    if (typeof sv === 'function') {
      const zones = sv('timeZone');
      if (Array.isArray(zones) && zones.length) return zones;
    }
  } catch {
    /* older runtime → broad fallback */
  }
  return COMMON_TZS.map((t) => t.id);
}

/** Display parts for one slot, resolved in the chosen display zone. */
export interface SlotView {
  /** `YYYY-MM-DD` in the display zone — the day grouping key. */
  dateISO: string;
  /** Short weekday, e.g. "Tue". */
  weekday: string;
  /** Day of month, e.g. 8. */
  dayNum: number;
  /** Short month, e.g. "Jul". */
  monthShort: string;
  /** Full human day, e.g. "Tuesday, 8 July". */
  dayFull: string;
  /** Start clock, e.g. "8:30 AM". */
  startTime: string;
  /** End clock, e.g. "9:00 AM". */
  endTime: string;
  /** "8:30 AM – 9:00 AM". */
  range: string;
  /** true when, in the display zone, the slot lands on a different calendar day
   *  than the anchor would suggest — surfaced as a "(next day)" hint. */
  crossesMidnight: boolean;
}

function partsIn(tz: string, iso: string): Record<string, string> {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(new Date(iso))) p[part.type] = part.value;
  return p;
}

function clock(tz: string, iso: string): string {
  const p = partsIn(tz, iso);
  const period = (p.dayPeriod || '').toUpperCase();
  return `${p.hour}:${p.minute} ${period}`.trim();
}

function dateKey(tz: string, iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** Resolve the display parts for one slot in `tz`. */
export function describeSlotView(
  startISO: string,
  endISO: string,
  tz: string,
): SlotView {
  const p = partsIn(tz, startISO);
  const startDay = dateKey(tz, startISO);
  const endDay = dateKey(tz, endISO);
  const weekdayShort = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'short',
  }).format(new Date(startISO));
  const startTime = clock(tz, startISO);
  const endTime = clock(tz, endISO);
  return {
    dateISO: startDay,
    weekday: weekdayShort,
    dayNum: parseInt(p.day, 10),
    monthShort: p.month,
    dayFull: `${p.weekday}, ${p.day} ${p.month}`,
    startTime,
    endTime,
    range: `${startTime} – ${endTime}`,
    crossesMidnight: endDay !== startDay,
  };
}

/** One day's grouped slots for the picker. */
export interface DayGroup<T> {
  dateISO: string;
  weekday: string;
  dayNum: number;
  monthShort: string;
  dayFull: string;
  slots: T[];
}

/**
 * Bucket a flat, ascending slot list into per-day groups in the display zone,
 * preserving order across and within days. Empty in → empty out.
 */
export function groupByDay<T extends { startISO: string; endISO: string }>(
  slots: T[],
  tz: string,
): DayGroup<T>[] {
  const groups: DayGroup<T>[] = [];
  const index = new Map<string, DayGroup<T>>();
  for (const s of slots) {
    const v = describeSlotView(s.startISO, s.endISO, tz);
    let g = index.get(v.dateISO);
    if (!g) {
      g = {
        dateISO: v.dateISO,
        weekday: v.weekday,
        dayNum: v.dayNum,
        monthShort: v.monthShort,
        dayFull: v.dayFull,
        slots: [],
      };
      index.set(v.dateISO, g);
      groups.push(g);
    }
    g.slots.push(s);
  }
  return groups;
}

/* ════════════════════ Add-to-calendar (real .ics, not a dead control) ════════════════════ */

function icsStamp(iso: string): string {
  // ICS UTC form: YYYYMMDDTHHMMSSZ
  return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function icsEscape(text: string): string {
  return text.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

/** Build a valid single-event VCALENDAR for the booking. Pure. */
export function buildIcs(params: {
  uid: string;
  startISO: string;
  endISO: string;
  summary: string;
  description: string;
  location: string;
}): string {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Aayatan Veda//Consult Door B//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${params.uid}`,
    `DTSTAMP:${icsStamp(new Date().toISOString())}`,
    `DTSTART:${icsStamp(params.startISO)}`,
    `DTEND:${icsStamp(params.endISO)}`,
    `SUMMARY:${icsEscape(params.summary)}`,
    `DESCRIPTION:${icsEscape(params.description)}`,
    `LOCATION:${icsEscape(params.location)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}
